import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import StyledButton from "../StyledButton/StyledButton";
import Spinner from "react-bootstrap/Spinner";
import { animated, useSpring } from "react-spring";
import styled, { withTheme } from "styled-components";

const Button = styled(StyledButton)`
  background-color: ${(props) => props?.$bgColor} !important;
  color: ${(props) => props?.theme?.white};
  ${(props) => (!props?.$keepBorder ? "border: none;" : "")}
  transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  &:hover {
    transform: scale(1.1);
    background-color: ${(props) => props?.$hoverBgColor} !important;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1), 0 6px 6px rgba(0, 0, 0, 0.12) !important;
    border-color: ${(props) => props?.$hoverBgColor} !important;
  }
  &:active {
    border-color: ${(props) => props?.$hoverBgColor} !important;
  }
  &.ultra-shadow {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    transition: all 0.2s ease-in-out;
  }
  &.ultra-shadow:hover {
    box-shadow: 0 14px 35px rgba(0, 0, 0, 0.25);
  }
`;

/**
 *  ** Animated button that uses promises
 *  @param {Object} theme The theme object to be used by the component
 *  @param {Function} onClick The onclick callback function
 *  @param {String} text Button text
 *  @param {String} icon FontAwesome icon string
 *  @param {Boolean} keepBorder If true will keep the button css border
 *  @param {ReactNode|ReactNodeArray} children - The computed children for this slot.
 */
const AnimatedButton = (props) => {
  const {
    theme,
    onClick,
    text,
    shadow,
    icon,
    keepBorder,
    children,
    isSuccess,
    setIsSuccess,
    isError,
    setIsError,
    variant,
    showProgress,
    disabled,
    className,
    size,
  } = props;
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (isSuccess) {
      setDone(true);
      setIsSuccess(false);
    }
    if (isError) {
      setDone(null);
      setIsError(false);
    }
  }, [isSuccess, isError, setIsError, setIsSuccess]);

  const handleClick = () => {
    if (done !== false) {
      setDone(false);
      onClick();
    }
  };

  const bgColor = useMemo(() => {
    let tempBgcolor = theme?.darkPurple;

    if (variant && variant !== "primary") {
      tempBgcolor = done ? theme?.dimGray : theme?.gray;
    }

    return tempBgcolor;
  }, [done, theme, variant]);

  const hoverBgColor = useMemo(() => {
    let tempHoverBgColor = theme?.persianIndigo;

    if (variant && variant !== "primary") {
      tempHoverBgColor = theme?.dimGray;
    }

    return tempHoverBgColor;
  }, [theme, variant]);

  const { x } = useSpring({
    x: done ? 1 : 0,
    opacity: done ? 1 : 0,
  });

  return (
    <Button
      data-testid={props["data-testid"]}
      onClick={handleClick}
      className={`btn ${shadow ? "ultra-shadow" : ""} ${className || ""}`}
      disabled={disabled}
      size={size ?? ""}
      $bgColor={bgColor}
      $hoverBgColor={hoverBgColor}
      $keepBorder={keepBorder}
    >
      {text || children}
      {icon && (
        <i className={`fas fa-${icon} icon-cta ms-2`} aria-hidden="true"></i>
      )}

      {showProgress && done === false && (
        <Spinner animation="border" size="sm" className="ms-2" />
      )}

      {showProgress && done === true && (
        <animated.i
          data-testid="animated-button-animated"
          style={{
            transform: x.interpolate(
              (interpolator) => `scale(${interpolator})`
            ),
            opacity: x.interpolate((interpolator) => interpolator),
          }}
          className="fas fa-check ms-2"
        />
      )}
    </Button>
  );
};

AnimatedButton.propTypes = {
  icon: PropTypes.string,
  showProgress: PropTypes.bool,
  disabled: PropTypes.bool,
};

AnimatedButton.defaultProps = {
  icon: "vial",
  showProgress: true,
  disabled: false,
};

export default withTheme(AnimatedButton);
